/**
 * @file planningTime.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-10-15
 * @description Tidying a typed time on the planning boards. Was a copy inside
 *              each board; one now, shared by both boards, the bulk editor,
 *              PlanTimeInput (collection and drop times, the Lorry day window).
 *
 *              1.2.0 (2026-10-15, Cam): "Allow 10.5 to go in as 10:30 and
 *              10.12 as 10:12 and 1012 and 1050 to be taken as 10:12 and
 *              10:50". parseTimeInput is the one rule:
 *                H / HH             → HH:00          ("9" → 09:00)
 *                HMM / HHMM         → HH:MM          ("930" → 09:30, "1050" → 10:50)
 *                H.M / H:M          → decimal hours  ("10.5" → 10:30, "9:2" → 09:12)
 *                H.MM / H:MM        → minutes        ("10.12" → 10:12)
 *                H. / H:            → HH:00
 *                HH:MM:SS           → HH:MM
 *                2pm, 2:30pm, 12am  → 24-hour clock
 *              Hours over 23 or minutes over 59 are refused with the reason —
 *              never turned silently into a different time.
 */

const pad = (n) => String(n).padStart(2, '0');

/**
 * Read a typed time. Returns { value: 'HH:MM' }, { error: 'why' }, or
 * { empty: true } when nothing was typed.
 */
export function parseTimeInput(raw) {
  const text = String(raw == null ? '' : raw).trim().toLowerCase().replace(/\s+/g, '');
  if (!text) return { empty: true };
  const ok = (h, m) => {
    if (!Number.isInteger(h) || h < 0 || h > 23) return { error: `${h} is not an hour — hours run from 00 to 23` };
    if (!Number.isInteger(m) || m < 0 || m > 59) return { error: `${pad(m)} is not a minute — minutes run from 00 to 59` };
    return { value: `${pad(h)}:${pad(m)}` };
  };

  // 12-hour clock: "2pm", "2:30pm", "12am", "2.30 p.m."
  const ampm = /^(\d{1,2})(?:[:.](\d{2}))?([ap])\.?(?:m\.?)?$/.exec(text);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = ampm[2] !== undefined ? Number(ampm[2]) : 0;
    if (h < 1 || h > 12) return { error: `${ampm[1]}${ampm[3]}m is not a time — a 12-hour clock runs from 1 to 12` };
    if (m > 59) return { error: `${ampm[2]} is not a minute — minutes run from 00 to 59` };
    if (ampm[3] === 'p' && h !== 12) h += 12;
    if (ampm[3] === 'a' && h === 12) h = 0;
    return ok(h, m);
  }

  let m;
  // "9", "09", "21"
  if ((m = /^(\d{1,2})$/.exec(text))) return ok(Number(m[1]), 0);
  // "930", "0930", "1012", "1050"
  if ((m = /^(\d{1,2})(\d{2})$/.exec(text))) return ok(Number(m[1]), Number(m[2]));
  // "9:" / "9."
  if ((m = /^(\d{1,2})[:.]$/.exec(text))) return ok(Number(m[1]), 0);
  // ONE digit after the separator: tenths of an hour — "10.5" → 10:30.
  if ((m = /^(\d{1,2})[:.](\d)$/.exec(text))) return ok(Number(m[1]), Number(m[2]) * 6);
  // Two digits after it: minutes — "10.12" → 10:12, "9:05" → 09:05.
  if ((m = /^(\d{1,2})[:.](\d{2})$/.exec(text))) return ok(Number(m[1]), Number(m[2]));
  // Seconds are dropped: "09:30:00" → 09:30.
  if ((m = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(text))) return ok(Number(m[1]), Number(m[2]));
  return { error: `"${String(raw).trim()}" is not a time — type it as 10:30, 1030, 10.30 or 10.5` };
}

/**
 * A typed time tidied to HH:MM; anything that is not a time is handed back as
 * typed (trimmed), so a caller can tell it was not read.
 */
export function normalizeTimeValue(value) {
  if (!value) return '';
  const r = parseTimeInput(value);
  return r.value || String(value).trim();
}

/*
 * What leaving a board's time field should do. Focusing the field fades the
 * time so a new one can be typed over it; leaving it:
 *   - with a valid time            → save it;
 *   - empty, but the user CLEARED it on purpose (Space, ".", Delete or
 *     Backspace pressed while in it) → clear the time;
 *   - empty otherwise (clicked in and out) → keep the old one;
 *   - something that is not a time → keep the old one and say why
 *     ({ action: 'invalid', error }).
 */
export const CLEAR_TIME_KEYS = new Set([' ', '.', 'Delete', 'Backspace']);

export function timeEditOutcome(typed, clearedOnPurpose) {
  const raw = String(typed ?? '');
  if (raw.replace(/[\s.]/g, '') === '') return clearedOnPurpose ? { action: 'clear' } : { action: 'restore' };
  const r = parseTimeInput(raw);
  return r.value ? { action: 'save', value: r.value } : { action: 'invalid', error: r.error };
}
